import { useState, useEffect, useContext } from "react";
import Swal from 'sweetalert2';
import {
    Backdrop,
    Paper,
    Stack,
    TextField,
    Typography,
    InputLabel,
    MenuItem,
    FormControl,
    Select,
} from "@mui/material";

import { getCategories, handleNumberInput, formatCurrency } from "../utils";

import CustomButton from "../custom_components/CustomButton";
import CustomModalHeader from "../custom_components/CustomModalHeader";

import { UserContext } from "../../context/UserContext";

export default function EditOrder({ open, onClose, onUpdate, orderInfo = [] }) {

    const apiUrl = process.env.REACT_APP_API_URL;
    const { categories } = useContext(UserContext);

    // Order input fields
    const [itemName, setItemName] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [category, setCategory] = useState("");
    const [price, setPrice] = useState("");
    const [sfee, setSF] = useState("");
    const [cargo, setCargo] = useState("");

    const [isSavingChanges, setIsSavingChanges] = useState(false);

    // USE TO DISPLAY ORDER INFO
    useEffect(() => {
        if (orderInfo) {
            setItemName(orderInfo.ItemName || "");
            setQuantity(orderInfo.ItemQty || "");
            setCategory(orderInfo.ItemCategory || "");
            setPrice(orderInfo.Price || "");
            setSF(orderInfo.SFee || "");
            setCargo(orderInfo.Cargo || "");
        }
    }, [open, orderInfo]);

    // CARGO PRICE
    const handleChangeCargo = (value) => {
        setCargo(value);

        if (value === "SC") {
            setSF("200");
        } else if (value === "AC") {
            setSF("400");
        }
    };

    // UPDATED ORDER INFO
    const handleUpdateOrder = async () => {
        setIsSavingChanges(true);

        try {
            const response = await fetch(`${apiUrl}manage_orders/edit_order.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    customerID: orderInfo.CustomerID,
                    orderID: orderInfo.OrderID,
                    orderInfo: {
                        itemName: itemName,
                        itemQty: quantity,
                        itemCategory: category,
                        itemPrice: price,
                        itemSFee: sfee,
                        itemCargo: cargo,
                    },
                })
            });

            const result = await response.json();

            if (result.stat === 'success') {

                onUpdate({
                    customerID: orderInfo.CustomerID,
                    orderID: orderInfo.OrderID,
                    ItemName: itemName,
                    ItemQty: quantity,
                    ItemCategory: category,
                    Price: price,
                    SFee: sfee,
                    Cargo: cargo,
                });

                Swal.fire({
                    icon: "info",
                    text: result.message,
                    timer: 3500,
                    showConfirmButton: false,
                });

                handleClose();

                return;
            }

            Swal.fire({ text: result.message, icon: 'error' });

        } catch (error) {
            Swal.fire({ title: 'Server Error!', text: error, icon: 'error' });
            console.error("Server error:", error);
        } finally {
            setIsSavingChanges(false);
        }
    };

    const handleClose = () => {
        setItemName("");
        setQuantity("");
        setCategory("");
        setPrice("");
        setSF("");
        setCargo("");

        onClose();
    }

    return (
        <Backdrop
            aria-hidden="false"
            open={open}
            sx={{
                zIndex: (theme) => theme.zIndex.drawer + 1,
                backdropFilter: "blur(1px)",
            }}
        >
            <Paper
                sx={{
                    width: 600,
                    p: 3,
                    borderRadius: 2,

                    position: "relative",
                    overflow: "hidden",
                    "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "4px",
                        background: "#2e3489"
                    }
                }}
            >

                <CustomModalHeader
                    title="Edit Order"
                    subtitle={`ID: ${orderInfo.OrderID}`}
                    onClose={onClose}
                />

                <Typography variant="subtitle1" mb={2} fontWeight={600}>
                    {orderInfo.CustomerName}
                </Typography>

                <Stack spacing={2}>

                    <TextField
                        fullWidth
                        autoComplete="off"
                        size="small"
                        label="Item Name"
                        value={itemName}
                        onChange={(e) => setItemName(e.target.value)}
                    />

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            size="small"
                            label="Quantity"
                            type="number"
                            value={quantity}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || Number(value) >= 0) {
                                    setQuantity(value);
                                }
                            }}
                            inputProps={{ min: 1 }}
                            onKeyDown={(e) => {
                                if (e.key === "-") {
                                    e.preventDefault();
                                }
                            }}
                        />
                        <FormControl fullWidth size="small">
                            <InputLabel>
                                Category
                            </InputLabel>

                            <Select
                                value={category}
                                label="Category"
                                onChange={(e) => setCategory(e.target.value)}
                            >
                                {categories.map((category) => (
                                    <MenuItem
                                        key={category.id}
                                        value={category.ItemCategory}
                                    >
                                        {category.ItemCategory}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            size="small"
                            label="Price"
                            value={price}
                            onChange={(e) => handleNumberInput(e, setPrice)}
                        />

                        <FormControl fullWidth size="small">
                            <InputLabel>
                                Cargo
                            </InputLabel>
                            <Select
                                value={cargo}
                                label="Cargo"
                                onChange={(e) => handleChangeCargo(e.target.value)}
                            >
                                <MenuItem value="SC">Sea Cargo</MenuItem>
                                <MenuItem value="AC">Air Cargo</MenuItem>
                                <MenuItem value="PH">Pinas</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            fullWidth
                            autoComplete="off"
                            size="small"
                            label="Shipping Fee"
                            value={sfee}
                            onChange={(e) => handleNumberInput(e, setSF)}
                        />

                        <Stack direction="row" width="100%">
                        </Stack>
                    </Stack>

                </Stack>

                {/* SUMMARY */}
                <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                        mt: 1,
                        p: 2,
                        borderRadius: 1.2,
                        border: "1px solid #ededed",
                        backgroundColor: "#fafafa",
                    }}
                >
                    <Stack spacing={0.5}>
                        <Typography
                            sx={{
                                fontSize: 13,
                                color: "#777",
                            }}
                        >
                            Total Amount
                        </Typography>

                        <Typography
                            sx={{
                                fontSize: 18,
                                fontWeight: 700,
                                color: "#059669",
                            }}
                        >
                            ₱{formatCurrency(
                                Number(price) *
                                Number(quantity) +
                                Number(sfee)
                            )}
                        </Typography>
                    </Stack>

                </Stack>

                {/* ACTIONS */}
                <Stack direction="row" spacing={2} mt={3} justifyContent="flex-end">
                    <CustomButton
                        label="Cancel"
                        variant="outlined"
                        hoverColor="#d14249"
                        onClick={handleClose}
                    />
                    <CustomButton
                        label={isSavingChanges ? "Updating" : "Save Changes"}
                        loading={isSavingChanges}
                        disabled={isSavingChanges}
                        onClick={handleUpdateOrder}
                    />
                </Stack>

            </Paper>

        </Backdrop>
    );
};